import { Box, Flex, Button, Checkbox, Text } from "@chakra-ui/react";
import { Modal } from "antd";
import OutlineButton from "components/Button/OutlineButton";
import PrimaryButton from "components/Button/PrimaryButton";
import {
  ImportarPlanilhaPrevisaoOrcamentariaRequest,
  ImportarPlanilhaPrevisaoOrcamentariaItem,
} from "core/features/previsao-orcamentaria/typings";
import useImportarPlanilhaPrevisaoOrcamentaria from "core/features/previsao-orcamentaria/hooks/useImportarPlanilhaPrevisaoOrcamentaria";
import { MdCloudDownload, MdAttachFile } from "react-icons/md";
import colors from "resources/theme/colors";
import { useForm } from "react-hook-form";
import Field from "components/Input/Field";
import CardDefault from "components/Cards/CardDefault";
import TabelaExemplo from "./components/TabelaExemplo";
import InputExcel from "components/Input/InputExcel";
import TabelaImportacao from "components/TabelaImportacao";
import { useImportarPlanilha } from "infra/helpers/useImportarPlanilha";
import { columnsImportacaoPlanilhaPrevisao } from "./helper/columnsTabela";
import { useLoading } from "ui/components/GlobalLoading";
import { showSuccess } from "infra/helpers/alerta";
import { useEffect } from "react";

interface ModalImportarPlanilhaProps {
  previsaoId: string;
  onClose: () => void;
  visible: boolean;
  refetch: any;
}

export default function ModalImportarPlanilha({
  previsaoId,
  onClose,
  visible,
  refetch,
}: ModalImportarPlanilhaProps) {
  const {
    data,
    proccessFile,
    onRequestError,
    onRowDelete,
    onRowInsertAbove,
    onRowInsertBelow,
    setData,
  } = useImportarPlanilha<ImportarPlanilhaPrevisaoOrcamentariaItem>();

  const { mutate: importarPlanilha, isLoading } =
    useImportarPlanilhaPrevisaoOrcamentaria(previsaoId);

  const { control, setValue, getValues } =
    useForm<ImportarPlanilhaPrevisaoOrcamentariaRequest>({
      defaultValues: {
        items: [],
        substituirLancamentos: true,
      },
    });

  const onSubmit = () => {
    const form = getValues();

    importarPlanilha(
      {
        substituirLancamentos: form.substituirLancamentos,
        items: data,
      },
      {
        onSuccess: () => {
          showSuccess().then(() => {
            refetch();
            onClose();
            setData([]);
          });
        },
        onError: (err) => {
          onRequestError(err);
        },
      }
    );
  };

  useEffect(() => {
    if (!visible) {
      setData([]);
      setValue("substituirLancamentos", true);
    }
  }, [setData, setValue, visible]);

  useLoading(isLoading);

  return (
    <Modal
      centered
      destroyOnClose
      width="900px"
      visible={visible}
      onCancel={onClose}
      title={`Importar planilha`}
      footer={
        <Flex flex={1} justify="end" gap={4}>
          <OutlineButton onClick={onClose}>Fechar</OutlineButton>
          <PrimaryButton onClick={onSubmit}>Importar</PrimaryButton>
        </Flex>
      }
    >
      <Flex mt={-8} minH="240px" flexDir="column">
        <Button
          w="fit-content"
          leftIcon={<MdCloudDownload fontSize={16} />}
          iconSpacing={2}
          color={colors.primary}
          bg={colors.black50}
          as="a"
          download
          href="https://octopus-assets.s3.amazonaws.com/importacao-previsao.xlsx"
          _hover={{
            bg: colors.black100,
          }}
          fontSize={14}
        >
          Baixar modelo de Planilha
        </Button>

        <Box my={4}>
          <Flex gap={6} alignItems="center">
            <InputExcel
              label="Escolher arquivo"
              onReadExcelFile={async (file) => {
                if (file) {
                  await proccessFile(file);
                }
              }}
              variant="outline"
              icon={<MdAttachFile />}
            />

            {data?.length ? (
              <>
                <Button
                  bg={colors.black100}
                  onClick={() => {
                    setValue("items", []);
                    setData([]);
                  }}
                >
                  Limpar
                </Button>
              </>
            ) : (
              <Text>Nenhum arquivo selecionado.</Text>
            )}
          </Flex>
        </Box>

        <Box mt="8px">
          <Field
            control={control}
            name="substituirLancamentos"
            render={({ field }) => (
              <Checkbox
                isChecked={field.value}
                onChange={() => field.onChange(!field.value)}
              >
                <Text fontSize="12px">Substituir lançamentos similares</Text>
              </Checkbox>
            )}
          />
        </Box>

        {!data.length ? (
          <CardDefault my={"16px"}>
            <TabelaExemplo />
          </CardDefault>
        ) : (
          <TabelaImportacao
            data={data}
            onRowDelete={onRowDelete}
            onRowInsertAbove={onRowInsertAbove}
            onRowInsertBelow={onRowInsertBelow}
            setData={setData}
            columns={columnsImportacaoPlanilhaPrevisao}
          />
        )}
      </Flex>
    </Modal>
  );
}
